import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, IconButton, Chip, Tooltip,
  CircularProgress, makeStyles, Box
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from '../../services/adminService';
import { PERMISSION_LABELS } from '../../config/permissions';
import AdminUserFormModal from './AdminUserFormModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import AdminLayout from '../../layouts/AdminLayout';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '32px 36px',
    minHeight: '100vh',
    background: theme.palette.background.default,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
    border: '1px solid rgba(16,185,129,0.2)',
    color: '#10b981',
    flexShrink: 0,
  },
  title: {
    fontWeight: 800,
    fontSize: '1.5rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '0.82rem',
    color: theme.palette.text.disabled,
    marginTop: 2,
  },
  addBtn: {
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: '9px 20px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      boxShadow: '0 6px 20px rgba(16,185,129,0.4)',
    },
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 38,
    padding: '0 10px',
    borderRadius: 12,
  },
  tablePaper: {
    borderRadius: 16,
    background: theme.palette.type === 'dark'
      ? 'rgba(30,41,59,0.5)'
      : 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(10px)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.08)'
      : '1px solid rgba(45,42,36,0.06)',
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 24px rgba(0,0,0,0.2)'
      : '0 4px 24px rgba(45,42,36,0.04)',
    overflow: 'hidden',
  },
  tableHead: {
    background: theme.palette.type === 'dark'
      ? 'rgba(15,23,42,0.6)'
      : 'rgba(248,250,252,0.9)',
  },
  headCell: {
    fontWeight: 700,
    fontSize: '0.72rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.disabled,
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.08)'
      : '1px solid rgba(45,42,36,0.06)',
    padding: '14px 20px',
  },
  bodyRow: {
    transition: 'background 0.15s ease',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'rgba(16,185,129,0.04)'
        : 'rgba(16,185,129,0.03)',
    },
  },
  bodyCell: {
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(148,163,184,0.06)'
      : '1px solid rgba(45,42,36,0.05)',
    padding: '14px 20px',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  nameCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  emailCell: {
    color: theme.palette.text.secondary,
    fontSize: '0.82rem',
  },
  roleChip: {
    fontSize: '0.72rem',
    height: 24,
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  permBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.78rem',
    color: '#10b981',
    fontWeight: 600,
    background: 'rgba(16,185,129,0.08)',
    borderRadius: 8,
    padding: '2px 10px',
  },
  editBtn: {
    color: theme.palette.text.disabled,
    '&:hover': { color: '#10b981', background: 'rgba(16,185,129,0.08)' },
    borderRadius: 8,
  },
  deleteBtn: {
    color: theme.palette.text.disabled,
    '&:hover': { color: '#ef4444', background: 'rgba(239,68,68,0.08)' },
    borderRadius: 8,
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: theme.palette.text.disabled,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 12,
    opacity: 0.3,
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
  },
  snackBar: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    zIndex: 9999,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    borderRadius: 12,
    padding: '12px 20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    boxShadow: '0 8px 24px rgba(16,185,129,0.3)',
    animation: '$fadeUp 0.3s ease',
  },
  '@keyframes fadeUp': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}));

function getRoleColor(role) {
  switch (role) {
    case 'admin':  return { background: 'rgba(212,168,83,0.15)', color: '#d4a853', border: '1px solid rgba(212,168,83,0.3)' };
    case 'editor': return { background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)' };
    default:       return { background: 'rgba(148,163,184,0.1)', color: '#94a3b8', border: '1px solid rgba(148,163,184,0.2)' };
  }
}

export default function AdminUserListPage() {
  const classes = useStyles();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdmins();
      setAdmins(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenCreate = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (admin) => {
    setEditData(admin);
    setModalOpen(true);
  };

  const handleSave = async (formData, id) => {
    if (id) {
      await updateAdmin(id, formData);
      showToast('Admin updated successfully');
    } else {
      await createAdmin(formData);
      showToast('Admin created successfully');
    }
    await load();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdmin(deleteTarget.id);
      showToast('Admin deleted');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <AdminLayout>
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <div className={classes.iconWrap}>
            <SupervisorAccountIcon />
          </div>
          <div>
            <Typography className={classes.title}>Admin Users</Typography>
            <Typography className={classes.subtitle}>
              Manage admin accounts and their section permissions
            </Typography>
          </div>
        </div>
        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Chip
            label={`Total Admins: ${admins.length}`}
            className={classes.countChip}
          />
          <Button
            variant="contained"
            className={classes.addBtn}
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Add Admin
          </Button>
        </Box>
      </div>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tablePaper} elevation={0}>
        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress style={{ color: '#10b981' }} size={32} />
          </div>
        ) : admins.length === 0 ? (
          <div className={classes.emptyState}>
            <SupervisorAccountIcon className={classes.emptyIcon} />
            <Typography>No admin users found. Add one to get started.</Typography>
          </div>
        ) : (
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.headCell}>#</TableCell>
                <TableCell className={classes.headCell}>Name</TableCell>
                <TableCell className={classes.headCell}>Email</TableCell>
                <TableCell className={classes.headCell}>Role</TableCell>
                <TableCell className={classes.headCell}>Permissions</TableCell>
                <TableCell className={classes.headCell} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin, idx) => {
                const roleStyle = getRoleColor(admin.role);
                const permCount = Array.isArray(admin.permissions) ? admin.permissions.length : 0;
                return (
                  <TableRow key={admin.id} className={classes.bodyRow}>
                    <TableCell className={classes.bodyCell} style={{ color: 'var(--text-disabled)', width: 48 }}>
                      {idx + 1}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Typography className={classes.nameCell}>{admin.name || '—'}</Typography>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Typography className={classes.emailCell}>{admin.email}</Typography>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Chip
                        label={(admin.role || 'editor').replace('_', ' ')}
                        size="small"
                        className={classes.roleChip}
                        style={roleStyle}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Tooltip
                        title={
                          Array.isArray(admin.permissions) && admin.permissions.length > 0
                            ? admin.permissions.map((k) => PERMISSION_LABELS[k] || k).join(', ')
                            : 'No permissions'
                        }
                        arrow
                      >
                        <span className={classes.permBadge}>
                          {permCount} section{permCount !== 1 ? 's' : ''}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={classes.bodyCell} align="right">
                      <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
                        <Tooltip title="Edit" arrow>
                          <IconButton size="small" className={classes.editBtn} onClick={() => handleOpenEdit(admin)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton size="small" className={classes.deleteBtn} onClick={() => setDeleteTarget(admin)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Form Modal */}
      <AdminUserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        editData={editData}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Admin User"
        message={`Are you sure you want to delete "${deleteTarget?.name || deleteTarget?.email}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Toast notification */}
      {toast && <div className={classes.snackBar}>{toast}</div>}
    </div>
    </AdminLayout>
  );
}
